/**
 * Time-of-day greeting bucketed into morning / afternoon / evening. Pulled
 * out as a pure function so it can be tested + the new-chat centered empty
 * state stays render-only.
 */
export function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// Label for the floating date pill that tracks the topmost visible message:
// today → "Today", yesterday → "Yesterday", same-year → "Jun 30", older →
// "Jun 30, 2024".
export function formatChatDateLabel(d: Date): string {
  const now = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, now)) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'Yesterday';
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * FALLBACK: derive a display name from the agent gateway URL the same way the
 * sidebar account button does (first subdomain label, everything before its
 * first `-`, capitalized). e.g. `wss://aivars-92a7765d.terminal.dime.gg` →
 * `Aivars`. Only meaningful for per-user gateway hosts (manual pairing);
 * hosts with a signed-in profile supply `config.user` and callers prefer
 * that — behind the shared gateway-proxy this would yield "Gateway".
 */
export function deriveGreetingName(url: string | null | undefined): string {
  if (url == null || url.length === 0) return '';
  const host = url.replace(/^wss?:\/\//, '').split(/[/:?#]/)[0] ?? '';
  const subdomain = host.split('.')[0] ?? '';
  const name = subdomain.split('-')[0] ?? '';
  return name.length > 0 ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

export function shortAddress(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

// Input types that aren't typing targets — focus moving to one of these is a
// click on a control, not an intent to type, so the composer should reclaim it.
export const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'range',
  'color',
  'file',
  'image',
]);

/**
 * Editable controls the user means to type into — focus must be allowed to
 * leave the composer for these (e.g. the Recents search box, inline rename
 * fields). Covers text inputs, textareas, selects, and contenteditable.
 */
export function isTextEntryTarget(el: Element | null): boolean {
  if (el == null) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return true;
  }
  if (el instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(el.type);
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

/**
 * Controls that run their own focus management — Radix menus / dialogs /
 * popovers (e.g. the model picker) and their triggers. Yanking focus back to
 * the composer mid-interaction would close them, so leave focus where it
 * landed.
 */
export function managesOwnFocus(el: Element | null): boolean {
  if (el == null) return false;
  if (
    el.closest(
      '[role="menu"],[role="menuitem"],[role="listbox"],[role="option"],[role="dialog"],[data-radix-popper-content-wrapper]',
    ) != null
  ) {
    return true;
  }
  return el.getAttribute('aria-haspopup') != null;
}

/**
 * Pull `market` strings off an opaque positions-feed payload. The feed is
 * `readonly unknown[]`; we read a `market` field defensively so this stays
 * decoupled from any host position shape.
 */
export function extractPositionMarkets(
  positions: readonly unknown[] | undefined,
): readonly string[] {
  if (positions == null) return [];
  const markets: string[] = [];
  for (const p of positions) {
    if (p != null && typeof p === 'object') {
      const market = (p as { readonly market?: unknown }).market;
      if (typeof market === 'string' && market.length > 0) {
        markets.push(market);
      }
    }
  }
  return markets;
}

interface ExamplePrompt {
  readonly label: string;
  readonly subtitle: string;
  readonly prompt: string;
}

/**
 * Build the four empty-state example chips, personalising them with the
 * user's current open positions when available. v1 reads only from the
 * positions feed (cheap, fully client-side); the prompts fall back to
 * a generic starter set when the user has no positions yet.
 */
export function buildExamplePrompts(
  positionMarkets: readonly string[],
): readonly ExamplePrompt[] {
  const topMarket = positionMarkets[0];
  const secondMarket = positionMarkets[1];
  const detailMarket = secondMarket ?? topMarket;

  return [
    topMarket != null
      ? {
          label: 'Current Position',
          subtitle: `Show my ${topMarket} position`,
          prompt: `Show my current ${topMarket} position with size, entry, mark price, and unrealized PnL as a positions_table.`,
        }
      : {
          label: 'Current Position',
          subtitle: 'Show my open positions',
          prompt:
            'Show my current open positions with size, entry, mark price, and unrealized PnL as a positions_table.',
        },
    {
      label: 'Available to Trade',
      subtitle: 'Show my available trading balance',
      prompt:
        "What's my available margin to open new positions? Render it as a single metric_card.",
    },
    {
      label: 'Account Value',
      subtitle: 'Show my current account value',
      prompt:
        "What's my current account value? Render it as a single metric_card.",
    },
    detailMarket != null
      ? {
          label: 'Position Detail',
          subtitle: `Show my ${detailMarket} entry vs mark price`,
          prompt: `Show my ${detailMarket} entry price and current mark price as labeled_output rows.`,
        }
      : {
          label: 'Funding',
          subtitle: 'How does perpetual funding work?',
          prompt:
            'Explain in markdown how perpetual funding works in 3 short paragraphs.',
        },
  ];
}
